"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.parseComment = exports.hasComment = exports.default = exports.RETURN_TYPES = exports.COMMENT_REGEX = exports.COMMENT_KEYS = void 0;
/* eslint-disable no-useless-escape */
/* ***************************
 * ENUMS & CONFIGS
 */

const STR_DELIMITER = '; ';
const STRING = 'string';
const NUMERIC = 'number';
const ARRAY = 'Array';
const FIRST_VALUE = 'FIRST_VALUE';
const COMMENT_KEYS = {
  RECTIFIER: 'RECTIFIER',
  AERIAL: 'AERIAL',
  VALVE: 'VALVE',
  TEST_STATION: 'TEST_STATION',
  LINE_MARKER: 'LINE_MARKER',
  FOREIGN_LINE: 'FOREIGN_LINE',
  PIPE_DEPTH: 'PIPE_DEPTH',
  AC: 'AC'
};
exports.COMMENT_KEYS = COMMENT_KEYS;
const defaultParser = v => v;
const COMMENTS = {
  [COMMENT_KEYS.RECTIFIER]: {
    regex: /RECTIFIER/gi,
    valueType: STRING,
    parser: defaultParser
  },
  [COMMENT_KEYS.AERIAL]: {
    regex: /AERIAL/gi,
    valueType: STRING,
    parser: defaultParser
  },
  [COMMENT_KEYS.VALVE]: {
    regex: /VALVE/gi,
    valueType: STRING,
    parser: defaultParser
  },
  [COMMENT_KEYS.TEST_STATION]: {
    regex: /TEST/gi,
    valueType: STRING,
    parser: defaultParser
  },
  [COMMENT_KEYS.LINE_MARKER]: {
    regex: /LINE MARKER/gi,
    valueType: STRING,
    parser: defaultParser
  },
  [COMMENT_KEYS.FOREIGN_LINE]: {
    regex: [/POWER LINE/gi, /RIVER/gi, /LINE CROSSING?/gi // Foreign Pipeline Crossing
    ],
    valueType: STRING,
    parser: defaultParser
  },
  [COMMENT_KEYS.PIPE_DEPTH]: {
    regex: /PIPE ?DEPTH=\d+(\.\d+)?(?=(\s|$))/i,
    valueType: NUMERIC,
    parser: str => {
      const depth = parseFloat(str.split('=')[1], 10);
      const isValidDepth = !Number.isNaN(depth);
      if (isValidDepth) {
        const formattedDepth = +depth.toFixed(2);
        return formattedDepth;
      }
      return undefined;
    }
  },
  [COMMENT_KEYS.AC]: {
    regex: [/(?<=(^|\s)\(?acv?(:|\/)\s*)\d+(\.(?=\d))?\d*(?=v\)?($|\s))/i, /(?<=RECONNECT AC:)( FG\/[\d\.]+(\/[\d\.]+)?)? NG\/[\d\.]+/i, /(?<=PS AC: )[\d\.]+/i],
    valueType: NUMERIC,
    parser: rawStr => {
      let str = rawStr;
      if (/ NG\//i.test(rawStr)) {
        [str] = rawStr.split(/ NG\//i)[1].match(/[\d\.]+/i);
      }
      const ac = Number.parseFloat(str, 10);
      if (!Number.isNaN(ac)) {
        return +ac.toFixed(4);
      }
      return undefined;
    }
  }
};
const COMMENT_REGEX = Object.keys(COMMENTS).reduce((acc, k) => {
  acc[k] = COMMENTS[k].regex;
  return acc;
}, {});
exports.COMMENT_REGEX = COMMENT_REGEX;
const RETURN_TYPES = {
  [STRING]: STRING,
  [ARRAY]: ARRAY,
  [FIRST_VALUE]: FIRST_VALUE
};

/* ***************************
 * HELPERS & UTILS
 */
exports.RETURN_TYPES = RETURN_TYPES;
const isDefined = v => v !== undefined && v !== null;
const _arrWrap = val => {
  if (!val || val instanceof Array) {
    return val;
  }
  return [val];
};
const _selectRegexes = key => {
  return _arrWrap((COMMENTS[key] || {}).regex);
};
const _selectParsers = key => _arrWrap((COMMENTS[key] || {}).parser || defaultParser);
const _selectValueType = key => (COMMENTS[key] || {}).valueType || STRING;

/* ***************************
 * boolean: HAS COMMENT = public
 */

const hasComment = _ref => {
  let {
    text,
    commentKey
  } = _ref;
  const regexes = _selectRegexes(commentKey);
  if (!regexes || !text) {
    return false;
  }
  return regexes.some(re => {
    const _re = new RegExp(re); // clone the regular expression to prevent state bugs caused by .test()
    return _re.test(text);
  });
};

/* ***************************
 * string: SUB PARSER - private
 */
exports.hasComment = hasComment;
const parseStrComment = _ref2 => {
  let {
    text,
    commentKey,
    returnType = STRING
  } = _ref2;
  if (!hasComment({
    text,
    commentKey
  })) {
    return undefined;
  }
  const regexes = _selectRegexes(commentKey);
  const parsers = _selectParsers(commentKey);
  const result = regexes.map((re, idx) => {
    const _re = new RegExp(re); // clone the regular expression to prevent state bugs caused by .exec()
    const parse = parsers[idx] || parsers[0];
    const matches = _re.exec(text);
    return matches ? parse(matches[0]) : undefined;
  }).filter(v => isDefined(v));
  switch (returnType) {
    case FIRST_VALUE:
      return result[0];
    case ARRAY:
      return result;
    case STRING:
      return result.join(STR_DELIMITER);
    default:
      return result.join(STR_DELIMITER);
  }
};

/* ***************************
 * numeric: SUB PARSER - private
 */

const parseNumComment = _ref3 => {
  let {
    text,
    commentKey,
    returnType = FIRST_VALUE
  } = _ref3;
  if (!hasComment({
    text,
    commentKey
  })) {
    return undefined;
  }
  const regexes = _selectRegexes(commentKey);
  const parsers = _selectParsers(commentKey);
  const result = regexes.map((re, idx) => {
    const _re = new RegExp(re); // clone the regular expression to prevent state bugs caused by .exec()
    const parse = parsers[idx] || parsers[0];
    const matches = _re.exec(text);
    return matches ? parse(matches[0]) : undefined;
  }).filter(v => isDefined(v));
  switch (returnType) {
    case ARRAY:
      return result;
    case FIRST_VALUE:
      return result[0];
    default:
      return result[0];
  }
};

/* ***************************
 * MAIN = public
 */

const parseComment = _ref4 => {
  let {
    text,
    commentKey,
    returnType
  } = _ref4;
  const valueType = _selectValueType(commentKey);
  switch (valueType) {
    case NUMERIC:
      return parseNumComment({
        text,
        commentKey,
        returnType
      });
    case STRING:
      return parseStrComment({
        text,
        commentKey,
        returnType
      });
    default:
      // return string parse by default
      return parseStrComment({
        text,
        commentKey,
        returnType
      });
  }
};
exports.parseComment = parseComment;
var _default = {
  COMMENT_KEYS,
  // dictionary of keys that can be used to select desired regex
  COMMENT_REGEX,
  // dictionary allowing direct access to regex by key
  RETURN_TYPES,
  // dictionary of possible return types FIRST_VALUE, ARRAY, STRING.  Numeric parsers only allow FIRST_VALUE & ARRAY.
  hasComment,
  parseComment
};
exports.default = _default;